
import React, { useEffect, useState, forwardRef, useImperativeHandle } from 'react';
import classNames from 'classnames';
import { DropdownHeader } from '../../components/DropdownHeader';
import { Text } from '../../components/base';
import { Empty } from '../../components/base';
import type { SkillMeta } from '../../global';

interface Props {
  inputValue: string;
  onPick: (skill: SkillMeta) => void;
}

export const SkillSlashPopover = forwardRef<
  { handleKeyDown: (event: React.KeyboardEvent) => void } | null,
  Props
>(({ inputValue, onPick }, ref) => {
  const isElectron = typeof window !== 'undefined' && window.electron?.isElectron;
  const active = isElectron && /^\/skill\b/i.test(inputValue.trim());
  const [skills, setSkills] = useState<SkillMeta[]>([]);
  const [hoverIndex, setHoverIndex] = useState(0);

  useEffect(() => {
    if (!active || !window.electron?.skillList) return;
    void window.electron.skillList().then((list) => {
      setSkills(list ?? []);
    });
  }, [active]);

  const query = inputValue.trim().replace(/^\/skill\b/i, '').trim().toLowerCase();
  const enabledSkills = skills.filter((s) => s.enabled);
  const filtered = query
    ? enabledSkills.filter((s) => s.name.toLowerCase().includes(query) || s.id.toLowerCase().includes(query))
    : enabledSkills;

  useEffect(() => {
    setHoverIndex(0);
  }, [query, skills.length]);


  useImperativeHandle(ref, () => ({
    handleKeyDown(event: React.KeyboardEvent) {
      if (!active || filtered.length === 0) return;
      switch (event.key) {
        case 'ArrowDown':
          setHoverIndex((i) => (i + 1) % filtered.length);
          event.preventDefault();
          break;
        case 'ArrowUp':
          setHoverIndex((i) => (i - 1 + filtered.length) % filtered.length);
          event.preventDefault();
          break;
        case 'Enter':
          if (hoverIndex >= 0 && hoverIndex < filtered.length) {
            onPick(filtered[hoverIndex]);
            event.preventDefault();
          }
          break;
      }
    },
  }));

  if (!active) return null;

  return (
    <ul role="listbox" className="ds-popover" data-state="show">
      <DropdownHeader title="스킬" />

      {filtered.length === 0 ? (
        <Empty
          image="images/ico_load.svg"
          desc={
            <>
              <Text as="strong" weight="semibold">
                사용할 수 있는 스킬이 없습니다
              </Text>
              <br />
              설정 → 스킬 탭에서 스킬을 등록하거나 활성화해주세요
            </>
          }
        />
      ) : (
        <div className="model-list">
          {filtered.map((s, index) => (
            <div
              key={s.id}
              tabIndex={0}
              className={classNames('model-list-item', { hover: index === hoverIndex })}
              onMouseEnter={() => {
                setHoverIndex(index);
              }}
            >
              <a
                className="model-item"
                onMouseDown={(e: React.MouseEvent<HTMLAnchorElement>) => {
                  e.preventDefault();
                  onPick(s);
                }}
              >
                <div className="title">
                  <strong className="name">{s.name}</strong>
                  <span className="desc">{s.description}</span>
                </div>
              </a>
            </div>
          ))}
        </div>
      )}
    </ul>
  );
});

SkillSlashPopover.displayName = 'SkillSlashPopover';

export default SkillSlashPopover;
